/**
 * ExpandableText - A component that manages expandable/collapsible text content
 *
 * This class handles the functionality for text content that can be expanded or collapsed
 * with a "Lire plus" / "Lire moins" button. It automatically detects if content is truncated
 * and only shows the CTA button when necessary.
 *
 */
export default class ExpandableText {
	private element: HTMLElement
	private content: HTMLElement
	private cta: HTMLElement
	private ctaText: HTMLElement
	private ctaCollapsedText: string
	private ctaExpandedText: string

	/**
	 * Creates a new ExpandableText instance
	 *
	 * @param options - Configuration options for the component
	 * @param options.element - The root HTML element containing the expandable text structure
	 */
	constructor({ element }: { element: HTMLElement }) {
		this.element = element
		this.content = element.querySelector('.expandableText-content') as HTMLElement
		this.cta = element.querySelector('.expandableText-cta') as HTMLElement
		this.ctaText = this.cta.querySelector('.cta-text') as HTMLElement
		this.ctaCollapsedText = this.cta.textContent || 'Lire plus'
		this.ctaExpandedText = this.cta.getAttribute('data-content-expanded') || 'Lire moins'

		this.onCtaClick = this.onCtaClick.bind(this)
	}

	/**
	 * Initializes the expandable text component
	 *
	 * Checks if the content is truncated (scrollHeight > clientHeight) and only shows
	 * the CTA button if the content actually needs to be expandable. If the content
	 * fits within its container, the CTA button remains hidden.
	 */
	init() {
		const isTruncated = this.content.scrollHeight > this.content.clientHeight

		if (isTruncated) {
			this.cta.classList.remove('hidden')
			this.cta.setAttribute('aria-expanded', 'false')
			this.addEvents()
		}
	}

	/**
	 * Adds event listeners to the component
	 */
	private addEvents() {
		this.cta.addEventListener('click', this.onCtaClick)
	}

	/**
	 * Handles the click event on the CTA button
	 */
	private onCtaClick() {
		this.element.classList.toggle('isExpanded')
		const isExpanded = this.element.classList.contains('isExpanded')
		this.cta.setAttribute('aria-expanded', isExpanded.toString())
		this.ctaText.textContent = isExpanded ? this.ctaExpandedText : this.ctaCollapsedText
		this.cta.setAttribute('aria-expanded', isExpanded.toString())
	}
}
